import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLazyGetFindemAuthQuery } from 'services/findemV2';
import { useLazyGetJobsQuery } from 'services/jobsV2';
import { userSelector, networksSelector } from 'redux/selectors';
import toast from 'react-hot-toast';

export const AGENT_OPTIONS = [
  { value: import.meta.env.VITE_FINDEM_CALIBRATION_AGENT_ID, label: 'calibration V6' },
  { value: import.meta.env.VITE_FINDEM_INTAKE_AGENT_ID, label: 'getro intake' },
];

const getInitialFormState = (networks) => {
  const urlParams = new URLSearchParams(window.location.search);
  const agentIdFromUrl = urlParams.get('agent_id') || '';
  const jobIdFromUrl = urlParams.get('job_id') || '';
  const collectionIdFromUrl = urlParams.get('collection_id') || '';

  // Compute initial selectedAgent
  let initialAgent = null;
  if (agentIdFromUrl) {
    const matchingAgent = AGENT_OPTIONS.find((opt) => opt.value === agentIdFromUrl);
    initialAgent = matchingAgent || { value: agentIdFromUrl, label: agentIdFromUrl };
  }

  // Compute initial selectedJob
  const initialJob = jobIdFromUrl ? { value: jobIdFromUrl, label: `Job ID: ${jobIdFromUrl}` } : null;

  // Compute initial selectedCollection
  let initialCollection = null;
  if (collectionIdFromUrl && networks) {
    const network =
      networks[collectionIdFromUrl] || Object.values(networks).find((n) => n.slug === collectionIdFromUrl);
    if (network) {
      initialCollection = { value: network.id, label: network.name || network.slug || network.id };
    } else {
      initialCollection = { value: collectionIdFromUrl, label: collectionIdFromUrl };
    }
  }

  return {
    queryParams: {
      jwt: urlParams.get('jwt') || '',
      agent_id: agentIdFromUrl,
      job_id: jobIdFromUrl,
      collection_id: collectionIdFromUrl,
      empty_state_title: urlParams.get('empty_state_title') || '',
      empty_state_subtitle: urlParams.get('empty_state_subtitle') || '',
      initial_prompts: urlParams.get('initial_prompts') || '',
      additional_params: '',
    },
    selectedAgent: initialAgent,
    selectedJob: initialJob,
    selectedCollection: initialCollection,
  };
};

export const usePublicAgentForm = () => {
  const user = useSelector(userSelector);
  const { networks } = useSelector(networksSelector);
  const isAuthenticated = !!user?.id;
  const [getFindemAuth] = useLazyGetFindemAuthQuery();
  const [getJobs] = useLazyGetJobsQuery();

  // Initialize all form state from URL params using lazy initialization
  const initialState = useMemo(() => getInitialFormState(networks), [networks]);
  const [queryParams, setQueryParams] = useState(initialState.queryParams);
  const [selectedAgent, setSelectedAgent] = useState(initialState.selectedAgent);
  const [selectedJob, setSelectedJob] = useState(initialState.selectedJob);
  const [selectedCollection, setSelectedCollection] = useState(initialState.selectedCollection);

  const [additionalParamsError, setAdditionalParamsError] = useState('');
  const [jobDefaultOptions, setJobDefaultOptions] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const collectionId = selectedCollection?.value || queryParams.collection_id;

  // Update selectedCollection when networks load and collection_id is in URL
  useEffect(() => {
    if (networks && queryParams.collection_id && !selectedCollection) {
      const network =
        networks[queryParams.collection_id] ||
        Object.values(networks).find((n) => n.slug === queryParams.collection_id);
      if (network) {
        setSelectedCollection({ value: network.id, label: network.name || network.slug || network.id });
      } else {
        setSelectedCollection({ value: queryParams.collection_id, label: queryParams.collection_id });
      }
    }
  }, [networks, queryParams.collection_id, selectedCollection]);

  // Prefill jwt if user is authenticated and collection is selected
  // Refetch JWT whenever collection changes
  useEffect(() => {
    const fetchJwt = async () => {
      // Clear token first when collection changes
      if (collectionId) {
        setQueryParams((prev) => ({
          ...prev,
          jwt: '',
        }));
      }

      if (isAuthenticated && collectionId) {
        try {
          const result = await getFindemAuth(collectionId).unwrap();

          if (result?.token) {
            setQueryParams((prev) => ({
              ...prev,
              jwt: result.token,
            }));
          }
        } catch (err) {
          // Silently fail - token is optional
        }
      }
    };

    fetchJwt();
  }, [isAuthenticated, collectionId, getFindemAuth]);

  // Validate additional params query string format
  const validateAdditionalParams = useCallback((value) => {
    if (!value) {
      setAdditionalParamsError('');
      return true;
    }

    // Remove leading ? or & if present
    const cleanValue = value.replace(/^[?&]+/, '');

    // Allow empty params
    if (cleanValue === '') {
      setAdditionalParamsError('');
      return true;
    }

    try {
      // Validate that it can be parsed as URLSearchParams
      const testParams = new URLSearchParams(cleanValue);

      // Check for basic query string format: key=value pairs separated by &
      const isValid = /^[^=&]+=[^&]*(&[^=&]+=[^&]*)*$/.test(cleanValue);

      if (!isValid) {
        setAdditionalParamsError('Invalid format. Use: param1=value1&param2=value2');
        return false;
      }

      // Ensure at least one param was parsed
      if (testParams.toString() === '') {
        setAdditionalParamsError('Invalid format. Use: param1=value1&param2=value2');
        return false;
      }

      setAdditionalParamsError('');
      return true;
    } catch (err) {
      setAdditionalParamsError('Invalid query string format');
      return false;
    }
  }, []);

  // Collection options from networks - only show networks with sourcing_agent feature flag
  const collectionOptions = useMemo(() => {
    if (!networks) return [];
    return Object.values(networks)
      .filter((network) => network.features?.includes('sourcing_agent'))
      .map((network) => ({
        value: network.id,
        label: network.name || network.slug || network.id,
      }));
  }, [networks]);

  const updatedLink = useMemo(() => {
    const baseUrl = `${window.location.origin}/shared-agent`;
    const params = new URLSearchParams();

    // Add collection_id if available
    if (collectionId) {
      params.set('collection_id', collectionId);
    }

    // Add agent_id if available
    if (queryParams.agent_id) {
      params.set('agent_id', queryParams.agent_id);
    }

    // Add job_id if available
    if (queryParams.job_id) {
      params.set('job_id', queryParams.job_id);
    }

    Object.entries(queryParams).forEach(([key, value]) => {
      if (value && key !== 'additional_params' && key !== 'collection_id' && key !== 'agent_id' && key !== 'job_id') {
        params.set(key, value);
      }
    });

    // Append and encode additional params if present and valid
    if (queryParams.additional_params && !additionalParamsError) {
      const cleanAdditionalParams = queryParams.additional_params.replace(/^[?&]+/, '');
      if (cleanAdditionalParams) {
        // Parse the additional params to properly encode them
        const additionalParamsObj = new URLSearchParams(cleanAdditionalParams);

        // Add each additional param to the main params object
        // This ensures proper URL encoding
        additionalParamsObj.forEach((value, key) => {
          params.set(key, value);
        });
      }
    }

    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }, [queryParams, collectionId, additionalParamsError]);

  const handleParamChange = useCallback(
    (key, value) => {
      setQueryParams((prev) => ({
        ...prev,
        [key]: value,
      }));

      // Validate additional params when it changes
      if (key === 'additional_params') {
        validateAdditionalParams(value);
      }
    },
    [validateAdditionalParams],
  );

  const handleAgentChange = useCallback(
    (option) => {
      setSelectedAgent(option);
      handleParamChange('agent_id', option?.value || '');
    },
    [handleParamChange],
  );

  const handleJobChange = useCallback(
    (option) => {
      setSelectedJob(option);
      handleParamChange('job_id', option?.value ? String(option.value) : '');
    },
    [handleParamChange],
  );

  const handleCollectionChange = useCallback(
    (option) => {
      setSelectedCollection(option);
      handleParamChange('collection_id', option?.value || '');
      // Reset job selection when network changes
      setSelectedJob(null);
      handleParamChange('job_id', '');
    },
    [handleParamChange],
  );

  const handleJobSearch = useCallback(
    async (searchValue) => {
      try {
        const attr = {
          page: 1,
          hitsPerPage: 20,
          filters: {
            ...(searchValue ? { q: searchValue } : {}),
          },
          networkId: collectionId, // Pass the selected network ID
        };
        const data = await getJobs(attr).unwrap();

        return (
          data?.map((job) => ({
            label: `${job.title} at ${job.organization?.name}`,
            value: Number(job.id),
          })) || []
        );
      } catch (error) {
        toast.error('Failed to load jobs');
        return [];
      }
    },
    [getJobs, collectionId],
  );

  const handleJobMenuOpen = useCallback(() => {
    setIsLoadingJobs(true);
    handleJobSearch('').then((data) => {
      setJobDefaultOptions(data);
      setIsLoadingJobs(false);
    });
  }, [handleJobSearch]);

  const handleOpenLink = useCallback(() => {
    window.open(updatedLink, '_blank', 'noopener,noreferrer');
  }, [updatedLink]);

  const handleGenerateToken = useCallback(async () => {
    try {
      const result = await getFindemAuth(collectionId).unwrap();

      if (result?.token) {
        handleParamChange('jwt', result.token);
      }
    } catch (err) {
      toast.error('Failed to generate calibration token');
    }
  }, [collectionId, getFindemAuth, handleParamChange]);

  // Check if all inputs are filled and valid
  const isAllInputsFilled =
    queryParams.collection_id &&
    queryParams.agent_id &&
    queryParams.jwt &&
    queryParams.job_id &&
    !additionalParamsError;

  return {
    queryParams,
    additionalParamsError,
    updatedLink,
    isAllInputsFilled,
    handleParamChange,
    handleOpenLink,
    handleGenerateToken,
    // Collection select
    selectedCollection,
    handleCollectionChange,
    collectionOptions,
    // Agent select
    selectedAgent,
    handleAgentChange,
    // Job search
    selectedJob,
    handleJobChange,
    handleJobSearch,
    handleJobMenuOpen,
    jobDefaultOptions,
    isLoadingJobs,
    collectionId,
  };
};
